
import { BusinessCaseService } from "@/services/BusinessCaseService";

import { container } from "tsyringe";
import moment from "moment";
import { Culture } from "@/services/Culture";
import { KendoHelpers } from "@/services/KendoHelpers";

import { defineComponent } from "vue";
import { useRoute, useRouter } from 'vue-router';
import { ref, watch } from 'vue';

import '@progress/kendo-ui/js/cultures/kendo.culture.cs-CZ';

import { NumericTextBox } from '@progress/kendo-inputs-vue-wrapper';

const businessCaseService = container.resolve<BusinessCaseService>(BusinessCaseService);

export default defineComponent({
  components: {
    'numerictextbox': NumericTextBox
  },  
  mounted: function(){
    window.kendo.culture("cs-CZ");
  },
  data() {
    return {
      busCase: null,
      soonerTime: null,
      extraTime: 30,
      delayTime: null,
      delayReasonId: null,
      delayReasons: [],

      realWeight: null,
      deliveryNote: ""
    };
  },

  computed: {
    /**
     * Cas zpozdeni (DATUM)
     */
    delayTimeDatePart(): string {
      return kendo.toString(this.delayTime, Culture.DATE_FORMAT);
    },

    /**
     * Cas zpozdeni (CAS)
     */
    delayTimeTimePart(): string {
      return kendo.toString(this.delayTime, Culture.TIME_FORMAT);
    },

    /**
     * Cas "mohu driv" (DATUM)
     */
    soonerTimeDatePart(): string {
      return kendo.toString(this.soonerTime, Culture.DATE_FORMAT);
    },

    /**
     * Cas "mohu driv" (CAS)
     */
    soonerTimeTimePart(): string {
      return kendo.toString(this.soonerTime, Culture.TIME_FORMAT);
    },

    /**
     * Extra time (hodiny)
     */
    extraTimeHours(): number {
      return Math.floor(this.extraTime / 60);
    },

    /**
     * Extra time (minuty)
     */
    extraTimeMinutes(): number {
      return Math.round((this.extraTime / 60 - this.extraTimeHours) * 60);
    },

    /**
     * LOAD/UNLOAD time (PLANNED)
     */
    luTime(): string {
      return this.busCase.isLoaded ? this.busCase.unloadTime : this.busCase.loadTime;
    },

    /**
     * Cas zpozdeni na nakladce/vykladce
     */
    luDelayTime(): string {
      // was loaded
      if (this.busCase.isLoaded) {
        if (this.busCase && this.busCase.currentUnloadDelayTime) {
          const t = kendo.parseDate(this.busCase.currentUnloadDelayTime);
          return kendo.toString(t, Culture.DATETIME_FORMAT);
        }
        return "";
      }
      // was not loaded
      if (this.busCase && this.busCase.currentLoadDelayTime) {
        const t = kendo.parseDate(this.busCase.currentLoadDelayTime);
        return kendo.toString(t, Culture.DATETIME_FORMAT);
      }
      return "";
    },

    /**
     * Misto nakladky/vykladky
     */
    luPlace(): string {
      return this.busCase.isLoaded
        ? this.busCase.subjectAddressName
        : this.busCase.loadAddressName;
    },

    /**
     * Pozadavek na drivejsi cas
     */
    queueSinceTime(): string {
      if (!this.busCase.queueId) {
        return "";
      }
      const t = kendo.parseDate(this.busCase.queueSince);
      return kendo.toString(t, Culture.DATETIME_FORMAT);
    },

    /**
     * Pozadavek na drivejsi cas
     */
    queueOfferedTime(): string {
      if (!this.busCase.queueId) {
        return "";
      }
      const t = kendo.parseDate(this.busCase.queueOfferedTime);
      return kendo.toString(t, Culture.DATETIME_FORMAT);
    },
  },

  // watch for changes on search (query)
  watch: { 
     '$route.params.refresh': {
        // Avoid using arrow functions when declaring watch callbacks as they will not have access to the component instance via this. - https://vuejs.org/api/options-state.html#watch
        handler: function()  {
          if(this.$route.query.refresh) {
            // clear query and refresh transport
            this.$router.replace({ query: null });
            this.readTransportDetail();
          }
        },
        deep: true,
        immediate: true
      }
  },

  async updated() {
    console.log("updated");
  },

  async created(): Promise<void> {
    this.readTransportDetail();
  },

  methods: {
    formatDateTime: Culture.dateTime,

    // read cases (transports)
    async readTransportDetail(): Promise<void> {
      try {
        let results = await Promise.all([
          businessCaseService.getOne(this.$route.params["id"] as string),
          businessCaseService.getDelayReasons(),
        ]);
        let c: any = results[0];

        // Zpozdeni pro nastaveni - bud load nebo unload podle stavu prepravy
        let delayString = c.isLoaded
          ? c.currentUnloadDelayTime
            ? c.currentUnloadDelayTime
            : c.unloadTime + " " + c.unloadTime
          : c.currentLoadDelayTime
          ? c.currentLoadDelayTime
          : c.loadTime + " " + c.loadTime;
        this.delayTime = kendo.parseDate(delayString);

        let dr = results[1].reverse();
        dr.push({ reasonId: null, name: "Proč došlo ke zpoždění " });
        this.delayReasons = dr.reverse();

        this.busCase = c;

        // MOHU DRIVE

        this.soonerTime = kendo.parseDate(this.luTime);
        this.extraTime = 30;
      } catch (e: any) {
        if (e.code == 403) {
          this.$router.push("/login");
        } else {
          this.$router.push("/");
        }
      }
    },

    /**
     * Minus 1 den u MOHU DRIVE
     * Nesmi by nizsi nez aktualni cas
     */
    minusSoonerDate(): void {
      const newSooner = kendo.date.addDays(this.soonerTime, -1);
      console.log("minussooneddate", this.soonerTime, newSooner);
      // nesmi se jit pred aktualni cas
      if (newSooner >= new Date()) {
        this.soonerTime = newSooner;
      }
    },

    /**
     * Plus 1 den u MOHU DRIVE
     * Nesmi byt vyssi nez aktualne prirazeny nakladaci/vykladaci cas
     */
    plusSoonerDate(): void {
      const newSooner = kendo.date.addDays(this.soonerTime, +1);
      const plannedTime = moment(this.luTime).toDate();
      if (newSooner < plannedTime) {
        this.soonerTime = newSooner;
      }
    },

    /**
     * Minus 10 minut u MOHU DRIVE
     * Nesmi byt nizsi nez aktualni cas
     */
    minusSoonerTime(): void {
      const prevDate = moment(this.soonerTime);
      const newDate = moment(this.soonerTime)
        .subtract(10, "minutes")
        .date(prevDate.get("date"))
        .month(prevDate.get("month"))
        .year(prevDate.get("year"))
        .toDate();
      // nesmi se jit pred aktualni datum
      if (newDate >= new Date()) {
        this.soonerTime = newDate;
      }
    },

    /**
     * Plus 10 minut u MOHU DRIVE
     * Nesmi byt vyssi nez aktualne prirazeny nakladaci/vykladaci cas
     */
    plusSoonerTime(): void {
      const prevDate = moment(this.soonerTime);
      const newDate = moment(this.soonerTime)
        .add(10, "minutes")
        .date(prevDate.get("date"))
        .month(prevDate.get("month"))
        .year(prevDate.get("year"))
        .toDate();
      // nesmi se jit pred planovane datum
      const plannedTime = moment(this.luTime).toDate();
      if (newDate < plannedTime) {
        this.soonerTime = newDate;
      }
    },

    /**
     * Extra time potrebny pro nalozeni (- 5 MINUT)
     */
    minusExtraTime(): void {
      const amount = 5;
      if (this.extraTime - amount >= 0) {
        this.extraTime = this.extraTime - amount;
      }
    },

    /**
     * Extra time potrebny pro nalozeni (+ 5 MINUT)
     */
    plusExtraTime(): void {
      this.extraTime += 5;
    },

    /**
     * MINUS 1 den
     */
    minusDelayDate(): void {
      const newDate = kendo.date.addDays(this.delayTime, -1);
      // nesmi se jit pred planovane datum
      const plannedTime = moment(this.luTime).toDate();
      if (newDate >= plannedTime) {
        this.delayTime = newDate;
      }
    },

    /**
     * PLUS 1 den
     */
    plusDelayDate(): void {
      this.delayTime = kendo.date.addDays(this.delayTime, +1);
    },

    /**
     * MINUS 10 minut
     */
    minusDelayTime(): void {
      const prevDate = moment(this.delayTime);
      const newDate = moment(this.delayTime)
        .subtract(10, "minutes")
        .date(prevDate.get("date"))
        .month(prevDate.get("month"))
        .year(prevDate.get("year"))
        .toDate();
      // nesmi se jit pred planovane datum
      const plannedTime = moment(this.luTime).toDate();
      if (newDate >= plannedTime) {
        this.delayTime = newDate;
      }
    },

    /**
     * PLUS 10 minut
     */
    plusDelayTime(): void {
      const prevDate = moment(this.delayTime);
      const newDate = moment(this.delayTime)
        .add(10, "minutes")
        .date(prevDate.get("date"))
        .month(prevDate.get("month"))
        .year(prevDate.get("year"))
        .toDate();
      this.delayTime = newDate;
    },

    /**
     * Set Loaded
     */
    async setLoaded(): Promise<void> {
      if(this.realWeight.toString().trim() === '') {
        kendo.alert("Zadejte naloženou váhu");
        return;
      }
      if(this.deliveryNote.trim() === '') {
        kendo.alert("Zadejte číslo dodacího listu");
        return;
      }
      this.realWeight = this.realWeight.replace(',', '.');
      let updatedCase: any = await businessCaseService.setLoaded(this.busCase.caseId, this.realWeight, this.deliveryNote);
      if(!updatedCase.error) {
        this.busCase = updatedCase;
      } else {
        kendo.alert(updatedCase.error.message);
      }
    },

    /**
     * Ulozit nastaveny cas zpozdeni
     */
    async save(): Promise<void> {
      const plannedTime = moment(this.luTime).toDate().getTime();
      const delayTime = this.delayTime.getTime();
      //console.log(delayTime, plannedTime, this.luTime, this.delayTime);
      if (delayTime <= plannedTime) {
        kendo.alert("Zpožděný čas musí být pozdější než očekávaný");
        return;
      }

      if (!this.delayReasonId) {
        kendo.alert("Vyberte důvod zpoždění");
        return;
      }

      let setDelayTime = this.delayTime;
      let updatedCase: any = await businessCaseService.saveDelay(
        this.busCase.caseId,
        this.delayTime,
        this.delayReasonId
      );
      if (!updatedCase.error) {
        //console.log('tf1 ', this.busCase.eventTimeFrom);
        this.busCase = updatedCase;
        //console.log('tf2 ', this.busCase.eventTimeFrom);


        this.delayReasonId = null;

        // Novy cas pro nahlaseni je bud ten zpozdeny, nebo pokud nemame zpozdeny udaj, doslo k posunu, tak posunuty
        let newDelayTime = this.busCase.isLoaded
          ? updatedCase.delayedUnloadTime
            ? updatedCase.delayedUnloadTime
            : updatedCase.unloadTime
          : updatedCase.delayedLoadTime
          ? updatedCase.delayedLoadTime
          : updatedCase.loadTime;
        this.delayTime = moment(newDelayTime).toDate();

        // Pokud byl event presunut, nastavime casy pro "MOHU DRIV"
        if (this.busCase.eventWasMoved) {
          let movedTime = moment(this.luTime).toDate();
          // Extra time defaultne na 30 minut
          this.extraTime = 30;
          this.soonerTime = movedTime;
          let agreed = await KendoHelpers.confirm(
            "Váš čas dojezdu byl posunut na " +
              kendo.toString(movedTime, Culture.DATETIME_FORMAT) +
              ". Souhlasíte s tímto časem?",
            "Dotaz",
            "Ano",
            "Ne"
          );
          if (!agreed) {
            let updatedCase: any = await businessCaseService.saveSoonerQueue(
              this.busCase.caseId,
              setDelayTime,
              0
            );
            if (!updatedCase.error) {
              this.busCase = updatedCase;
            } else {
              kendo.alert(updatedCase.error.message);
            }
          }
        }
      } else {
        kendo.alert(updatedCase.error.message);
      }
    },

    /**
     * Save Sooner time + extra time (to queue) and close the dialog
     */
    async saveSoonerTime(): Promise<void> {

      // Kontrola, aby mohu drive bylo opravdu drive
      const currentEventTimeFrom = moment(this.busCase.eventTimeFrom).toDate().getTime();
      const soonerTime = (this.soonerTime as Date).getTime();
      if(soonerTime >= currentEventTimeFrom) {
        kendo.alert('Čas "Mohu dříve" musí být dřívější, než je aktuálně naplánovaný čas.');
        return;
      }

      let updatedCase: any = await businessCaseService.saveSoonerQueue(
        this.busCase.caseId,
        this.soonerTime,
        this.extraTime
      );

      if (!updatedCase.error) {
        this.busCase = updatedCase;
        document.getElementById("wantSoonerDismiss").click();
      } else {
        kendo.alert(updatedCase.error.message);
      }
    },

    /**
     * Accept offered time
     */
    async acceptTime(): Promise<void> {
      if (this.busCase.queueOffered) {
        let updatedCase = await businessCaseService.acceptQueueOffer(
          this.busCase.queueId
        );
        if (!updatedCase.error) {
          this.busCase = updatedCase;
          this.delayTime = moment(updatedCase.eventTimeFrom).toDate();
          this.soonerTime = moment(updatedCase.eventTimeFrom).toDate();
        } else {
          kendo.alert(updatedCase.error.message);
        }
      }
    },

    /**
     * Reject offered time
     */
    async rejectTime(): Promise<void> {
      if (this.busCase.queueOffered) {
        let updatedCase = await businessCaseService.rejectQueueOffer(
          this.busCase.queueId
        );
        if (!updatedCase.error) {
          this.busCase = updatedCase;
        } else {
          kendo.alert(updatedCase.error.message);
        }
      }
    },
  },
});
